import {
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  Image,
  Input,
  Stack,
  Text,
} from '@chakra-ui/react'
import {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import mangoLogo from '~/assets/mango-logo-gradient.png'
import { useMangoApiClient } from '~/atoms/MangoApi'
import { DetailViewWrapper } from '~/components/compositional/CommonWrappers'
import { Content } from '~/components/compositional/DetailViewStyles'
import { LazyPdfViewer } from '~/components/Pdfs/LazyPdfViewer'
import { openInNewTab } from '~/utils/functions'
import {
  SecureFaxDownloadResponse,
  SecureFaxErrorCode,
  SecureFaxErrorResponse,
  SecureFaxStatus,
} from '~/utils/mangoApiClient/mangoApiClient'

import { ApiError } from '@mangovoice/types'

type SecureFaxViewState =
  | 'checking_status'
  | 'status_invalid'
  | 'awaiting_password'
  | 'submitting_password'
  | 'download_ready'
  | 'download_fetch_error'

type InvalidStatusView =
  | 'expired'
  | 'disabled'
  | 'not_found'
  | 'max_downloads_reached'
  | 'too_many_attempts'
  | 'throttled'
  | 'unknown'

type FormErrorCode =
  | 'password_required'
  | 'invalid_password'
  | 's3_error'
  | 'throttled'
  | 'unknown'

interface SecureFaxDownloadPreview {
  fromNumber: string
  toNumber: string
  receivedAt: string
  thumbnailUrl: string
  downloadUrl: string
  downloadsRemaining: number
}

interface SecureFaxMockPreview extends SecureFaxDownloadPreview {
  previewUrl: string
}

interface SecureFaxPageProps {
  mockPreview?: SecureFaxMockPreview
}

const secureFaxStatusErrorCodes = new Set<SecureFaxStatus>([
  'not_found',
  'expired',
  'disabled',
  'max_downloads_reached',
  'too_many_attempts',
])

const secureFaxErrorCodes = new Set<SecureFaxErrorCode>([
  'password_required',
  'invalid_password',
  'too_many_attempts',
  'max_downloads_reached',
  'expired',
  'disabled',
  'not_found',
  's3_error',
  'throttled',
])

const invalidStatusTranslationKeys = {
  expired: {
    title: 'secureFaxPage.status.expired.title',
    description: 'secureFaxPage.status.expired.description',
  },
  disabled: {
    title: 'secureFaxPage.status.disabled.title',
    description: 'secureFaxPage.status.disabled.description',
  },
  not_found: {
    title: 'secureFaxPage.status.not_found.title',
    description: 'secureFaxPage.status.not_found.description',
  },
  max_downloads_reached: {
    title: 'secureFaxPage.status.max_downloads_reached.title',
    description: 'secureFaxPage.status.max_downloads_reached.description',
  },
  too_many_attempts: {
    title: 'secureFaxPage.status.too_many_attempts.title',
    description: 'secureFaxPage.status.too_many_attempts.description',
  },
  throttled: {
    title: 'secureFaxPage.status.throttled.title',
    description: 'secureFaxPage.status.throttled.description',
  },
  unknown: {
    title: 'secureFaxPage.status.unknown.title',
    description: 'secureFaxPage.status.unknown.description',
  },
} as const

const submitErrorTranslationMap = {
  password_required: 'secureFaxPage.errors.password_required',
  invalid_password: 'secureFaxPage.errors.invalid_password',
  s3_error: 'secureFaxPage.errors.s3_error',
  throttled: 'secureFaxPage.errors.throttled',
  unknown: 'secureFaxPage.errors.unknown',
} as const

const mapDownloadResponse = (
  response: SecureFaxDownloadResponse,
): SecureFaxDownloadPreview => {
  return {
    fromNumber: response.from_number,
    toNumber: response.to_number,
    receivedAt: response.received_at,
    thumbnailUrl: response.thumbnail_url,
    downloadUrl: response.download_url,
    downloadsRemaining: response.downloads_remaining,
  }
}

const getErrorCode = (
  error?: SecureFaxErrorResponse | ApiError,
): SecureFaxErrorCode | 'unknown' => {
  if (!error || typeof error !== 'object') {
    return 'unknown'
  }

  const maybeCode = (error as { code?: string }).code
  if (maybeCode && secureFaxErrorCodes.has(maybeCode as SecureFaxErrorCode)) {
    return maybeCode as SecureFaxErrorCode
  }

  return 'unknown'
}

const getErrorMessage = (error?: SecureFaxErrorResponse | ApiError): string => {
  if (!error || typeof error !== 'object') {
    return ''
  }

  const secureFaxError = error as SecureFaxErrorResponse
  return secureFaxError.detail || secureFaxError.message || ''
}

const toFormErrorCode = (
  errorCode: SecureFaxErrorCode | 'unknown',
): FormErrorCode => {
  switch (errorCode) {
    case 'password_required':
    case 'invalid_password':
    case 's3_error':
    case 'throttled':
      return errorCode
    default:
      return 'unknown'
  }
}

const formatReceivedAt = (receivedAt: string): string => {
  const parsedDate = new Date(receivedAt)

  if (Number.isNaN(parsedDate.valueOf())) {
    return receivedAt
  }

  return parsedDate.toLocaleString()
}

export const SecureFaxPage = ({ mockPreview }: SecureFaxPageProps = {}) => {
  const { t } = useTranslation()
  const api = useMangoApiClient()
  const { token } = useParams<{ token: string }>()
  const isMockMode = !!mockPreview

  const [viewState, setViewState] =
    useState<SecureFaxViewState>('checking_status')
  const [invalidStatus, setInvalidStatus] =
    useState<InvalidStatusView>('unknown')
  const [password, setPassword] = useState('')
  const [submitErrorCode, setSubmitErrorCode] = useState<FormErrorCode | null>(
    null,
  )
  const [submitErrorMessage, setSubmitErrorMessage] = useState('')
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(
    null,
  )
  const [downloadPreview, setDownloadPreview] =
    useState<SecureFaxDownloadPreview | null>(null)
  const [previewObjectUrl, setPreviewObjectUrl] = useState<string | null>(null)
  const [downloadFetchError, setDownloadFetchError] = useState('')

  const previewObjectUrlRef = useRef<string | null>(null)

  const revokePreviewObjectUrl = useCallback(() => {
    if (previewObjectUrlRef.current?.startsWith('blob:')) {
      URL.revokeObjectURL(previewObjectUrlRef.current)
    }
    previewObjectUrlRef.current = null
    setPreviewObjectUrl(null)
  }, [])

  const resetSubmitErrorState = useCallback(() => {
    setSubmitErrorCode(null)
    setSubmitErrorMessage('')
    setAttemptsRemaining(null)
  }, [])

  const fetchPreviewBlob = useCallback(
    async (downloadUrl: string): Promise<string> => {
      const previewResponse = await fetch(downloadUrl)

      if (!previewResponse.ok) {
        throw new Error(`Failed to fetch preview (${previewResponse.status})`)
      }

      const previewBlob = await previewResponse.blob()
      const isPdfBlob = previewBlob.type.toLowerCase().includes('pdf')
      const normalizedPreviewBlob = isPdfBlob
        ? previewBlob
        : new Blob([previewBlob], { type: 'application/pdf' })

      return URL.createObjectURL(normalizedPreviewBlob)
    },
    [],
  )

  const checkTokenStatus = useCallback(async () => {
    if (isMockMode && mockPreview) {
      revokePreviewObjectUrl()
      setDownloadFetchError('')
      resetSubmitErrorState()
      setPassword('')
      setDownloadPreview({
        fromNumber: mockPreview.fromNumber,
        toNumber: mockPreview.toNumber,
        receivedAt: mockPreview.receivedAt,
        thumbnailUrl: mockPreview.thumbnailUrl,
        downloadUrl: mockPreview.downloadUrl,
        downloadsRemaining: mockPreview.downloadsRemaining,
      })
      previewObjectUrlRef.current = mockPreview.previewUrl
      setPreviewObjectUrl(mockPreview.previewUrl)
      setViewState('download_ready')
      return
    }

    revokePreviewObjectUrl()
    setDownloadPreview(null)
    setDownloadFetchError('')
    resetSubmitErrorState()
    setPassword('')

    if (!token) {
      setInvalidStatus('not_found')
      setViewState('status_invalid')
      return
    }

    setViewState('checking_status')

    let statusResponse
    try {
      statusResponse = await api.auth.getSecureFaxStatus(token)
    } catch {
      setInvalidStatus('unknown')
      setViewState('status_invalid')
      return
    }

    if (!statusResponse.ok) {
      const errorCode = getErrorCode(
        statusResponse.data as SecureFaxErrorResponse | ApiError | undefined,
      )
      if (errorCode === 'throttled') {
        setInvalidStatus('throttled')
      } else if (secureFaxStatusErrorCodes.has(errorCode as SecureFaxStatus)) {
        setInvalidStatus(errorCode as InvalidStatusView)
      } else {
        setInvalidStatus('unknown')
      }
      setViewState('status_invalid')
      return
    }

    const secureFaxStatus = statusResponse.data?.status

    if (secureFaxStatus === 'valid') {
      setViewState('awaiting_password')
      return
    }

    if (secureFaxStatus && secureFaxStatusErrorCodes.has(secureFaxStatus)) {
      setInvalidStatus(secureFaxStatus as InvalidStatusView)
    } else {
      setInvalidStatus('unknown')
    }

    setViewState('status_invalid')
  }, [
    api,
    isMockMode,
    mockPreview,
    resetSubmitErrorState,
    revokePreviewObjectUrl,
    token,
  ])

  const handlePasswordSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault()

      if (!token) {
        setInvalidStatus('not_found')
        setViewState('status_invalid')
        return
      }

      resetSubmitErrorState()

      if (!password) {
        setSubmitErrorCode('password_required')
        return
      }

      setViewState('submitting_password')

      let downloadResponse
      try {
        downloadResponse = await api.auth.downloadSecureFax(token, password)
      } catch {
        setSubmitErrorCode('unknown')
        setSubmitErrorMessage(t('secureFaxPage.errors.unknown'))
        setViewState('awaiting_password')
        return
      }

      if (!downloadResponse.ok) {
        const errorCode = getErrorCode(
          downloadResponse.data as
            SecureFaxErrorResponse | ApiError | undefined,
        )

        if (secureFaxStatusErrorCodes.has(errorCode as SecureFaxStatus)) {
          setInvalidStatus(errorCode as InvalidStatusView)
          setViewState('status_invalid')
          return
        }

        setSubmitErrorCode(toFormErrorCode(errorCode))
        setSubmitErrorMessage(
          getErrorMessage(
            downloadResponse.data as
              SecureFaxErrorResponse | ApiError | undefined,
          ),
        )

        const remaining = (downloadResponse.data as SecureFaxErrorResponse)
          ?.attempts_remaining
        if (errorCode === 'invalid_password' && typeof remaining === 'number') {
          setAttemptsRemaining(remaining)
        }

        setViewState('awaiting_password')
        return
      }

      if (!downloadResponse.data) {
        setSubmitErrorCode('unknown')
        setViewState('awaiting_password')
        return
      }

      const secureFaxDownload = mapDownloadResponse(downloadResponse.data)
      setDownloadPreview(secureFaxDownload)

      try {
        const objectUrl = await fetchPreviewBlob(secureFaxDownload.downloadUrl)
        revokePreviewObjectUrl()
        previewObjectUrlRef.current = objectUrl
        setPreviewObjectUrl(objectUrl)
        setViewState('download_ready')
        setPassword('')
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : t('secureFaxPage.download.fetchError.generic')
        setDownloadFetchError(message)
        setViewState('download_fetch_error')
      }
    },
    [
      api,
      fetchPreviewBlob,
      password,
      resetSubmitErrorState,
      revokePreviewObjectUrl,
      t,
      token,
    ],
  )

  const handleRetryPreviewFetch = useCallback(async () => {
    if (!downloadPreview) {
      return
    }

    setDownloadFetchError('')

    try {
      const objectUrl = await fetchPreviewBlob(downloadPreview.downloadUrl)
      revokePreviewObjectUrl()
      previewObjectUrlRef.current = objectUrl
      setPreviewObjectUrl(objectUrl)
      setViewState('download_ready')
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : t('secureFaxPage.download.fetchError.generic')
      setDownloadFetchError(message)
      setViewState('download_fetch_error')
    }
  }, [downloadPreview, fetchPreviewBlob, revokePreviewObjectUrl, t])

  const handleDownloadClick = useCallback(() => {
    if (!previewObjectUrl) {
      return
    }

    const anchor = document.createElement('a')
    anchor.href = previewObjectUrl
    anchor.download = 'secure-fax.pdf'

    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
  }, [previewObjectUrl])

  const handleOpenInNewTabClick = useCallback(() => {
    openInNewTab(previewObjectUrl ?? undefined)
  }, [previewObjectUrl])

  const invalidStatusTranslation = useMemo(() => {
    const translationKeys = invalidStatusTranslationKeys[invalidStatus]

    return {
      title: t(translationKeys.title),
      description: t(translationKeys.description),
    }
  }, [invalidStatus, t])

  const submitErrorText = useMemo(() => {
    if (!submitErrorCode) {
      return ''
    }

    const translationKey =
      submitErrorTranslationMap[submitErrorCode] ??
      submitErrorTranslationMap.unknown
    const fallbackText = t('secureFaxPage.errors.unknown')
    const translated = t(translationKey)

    return translated === translationKey ? fallbackText : translated
  }, [submitErrorCode, t])

  useEffect(() => {
    void checkTokenStatus()
  }, [checkTokenStatus])

  useEffect(() => {
    return () => {
      revokePreviewObjectUrl()
    }
  }, [revokePreviewObjectUrl])

  const renderStatusInvalidState = () => {
    return (
      <Stack gap={4} alignItems="center" textAlign="center">
        <Heading size="lg">{invalidStatusTranslation.title}</Heading>
        <Text color="fg.muted">{invalidStatusTranslation.description}</Text>
        <Button onClick={() => void checkTokenStatus()}>
          {t('secureFaxPage.status.actions.checkAgain')}
        </Button>
      </Stack>
    )
  }

  const renderPasswordForm = () => {
    const isSubmitting = viewState === 'submitting_password'

    return (
      <Stack
        as="form"
        gap={4}
        alignItems="center"
        textAlign="center"
        onSubmit={(event) => void handlePasswordSubmit(event)}
      >
        <Heading size="lg">{t('secureFaxPage.password.title')}</Heading>
        <Text color="fg.muted">{t('secureFaxPage.password.description')}</Text>

        <Stack gap={2} w="100%" textAlign="left">
          <label htmlFor="secure-fax-password" style={{ fontWeight: '500' }}>
            {t('secureFaxPage.password.label')}
          </label>
          <Input
            id="secure-fax-password"
            type="password"
            placeholder={t('secureFaxPage.password.placeholder')}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
          />
        </Stack>

        {submitErrorText ? (
          <Text color="red.solid">{submitErrorText}</Text>
        ) : null}

        {attemptsRemaining !== null ? (
          <Text color="fg.muted">
            {t('secureFaxPage.errors.attemptsRemaining', {
              count: attemptsRemaining,
            })}
          </Text>
        ) : null}

        {submitErrorMessage ? (
          <Text color="fg.muted">{submitErrorMessage}</Text>
        ) : null}

        <Button type="submit" loading={isSubmitting}>
          {isSubmitting
            ? t('secureFaxPage.password.submittingButton')
            : t('secureFaxPage.password.submitButton')}
        </Button>
      </Stack>
    )
  }

  const renderDownloadDetails = () => {
    if (!downloadPreview) {
      return null
    }

    return (
      <Grid templateColumns={{ base: '1fr 1fr', lg: 'repeat(4, 1fr)' }} gap={3}>
        <Box
          borderWidth="1px"
          borderColor="border.subtle"
          borderRadius="md"
          p={3}
        >
          <Text fontSize="sm" color="fg.muted" mb={1}>
            {t('secureFaxPage.download.fromLabel')}:
          </Text>
          <Text fontWeight="semibold">{downloadPreview.fromNumber}</Text>
        </Box>
        <Box
          borderWidth="1px"
          borderColor="border.subtle"
          borderRadius="md"
          p={3}
        >
          <Text fontSize="sm" color="fg.muted" mb={1}>
            {t('secureFaxPage.download.toLabel')}:
          </Text>
          <Text fontWeight="semibold">{downloadPreview.toNumber}</Text>
        </Box>
        <Box
          borderWidth="1px"
          borderColor="border.subtle"
          borderRadius="md"
          p={3}
        >
          <Text fontSize="sm" color="fg.muted" mb={1}>
            {t('secureFaxPage.download.receivedLabel')}:
          </Text>
          <Text fontWeight="semibold">
            {formatReceivedAt(downloadPreview.receivedAt)}
          </Text>
        </Box>
        <Box
          borderWidth="1px"
          borderColor="border.subtle"
          borderRadius="md"
          p={3}
        >
          <Text fontSize="sm" color="fg.muted" mb={1}>
            {t('secureFaxPage.download.downloadsRemainingLabel')}:
          </Text>
          <Text fontWeight="semibold">
            {downloadPreview.downloadsRemaining}
          </Text>
        </Box>
      </Grid>
    )
  }

  const renderDownloadReadyState = () => {
    if (!downloadPreview || !previewObjectUrl) {
      return null
    }

    return (
      <Stack gap={5}>
        <Stack gap={2} alignItems="center" textAlign="center">
          <Heading size="lg">{t('secureFaxPage.download.title')}</Heading>
          <Text color="fg.muted">
            {t('secureFaxPage.download.description')}
          </Text>
        </Stack>

        {renderDownloadDetails()}

        <Box
          w="100%"
          h={{ base: '560px', md: 'calc(100vh - 260px)' }}
          minH={{ base: '560px', md: '620px' }}
          maxH={{ base: 'none', md: '82vh' }}
          borderWidth="1px"
          borderColor="border.subtle"
          borderRadius="md"
          overflow="hidden"
          bg="bg.subtle"
          display="flex"
        >
          <DetailViewWrapper
            style={{ height: '100%', minHeight: 0, width: '100%' }}
          >
            <Content style={{ height: '100%', minHeight: 0 }}>
              <LazyPdfViewer
                file={previewObjectUrl}
                filename="secure-fax.pdf"
              />
            </Content>
          </DetailViewWrapper>
        </Box>

        <Stack gap={3}>
          <Button onClick={handleDownloadClick}>
            {t('secureFaxPage.download.actions.download')}
          </Button>
          <Button variant="outline" onClick={handleOpenInNewTabClick}>
            {t('secureFaxPage.download.actions.openInNewTab')}
          </Button>
          {!isMockMode ? (
            <Button
              variant="ghost"
              onClick={() => setViewState('awaiting_password')}
            >
              {t('secureFaxPage.download.actions.tryAnotherPassword')}
            </Button>
          ) : null}
        </Stack>
      </Stack>
    )
  }

  const renderDownloadFetchErrorState = () => {
    return (
      <Stack gap={4} alignItems="center" textAlign="center">
        <Heading size="lg">
          {t('secureFaxPage.download.fetchError.title')}
        </Heading>
        <Text color="fg.muted">
          {t('secureFaxPage.download.fetchError.description')}
        </Text>

        {downloadFetchError ? (
          <Text color="red.solid">{downloadFetchError}</Text>
        ) : null}

        <Box w="100%" maxW="360px" textAlign="left">
          {renderDownloadDetails()}
        </Box>

        <Flex wrap="wrap" gap={3}>
          <Button onClick={() => void handleRetryPreviewFetch()}>
            {t('secureFaxPage.download.fetchError.retryPreview')}
          </Button>
          <Button variant="outline" onClick={handleOpenInNewTabClick}>
            {t('secureFaxPage.download.actions.openInNewTab')}
          </Button>
          <Button
            variant="ghost"
            onClick={() => setViewState('awaiting_password')}
          >
            {t('secureFaxPage.download.actions.tryAnotherPassword')}
          </Button>
        </Flex>
      </Stack>
    )
  }

  return (
    <Flex h="100vh" overflowY="auto" bg="bg.subtle" p={{ base: 4, md: 8 }}>
      <Box
        w="100%"
        maxW={
          viewState === 'download_ready' || viewState === 'download_fetch_error'
            ? '800px'
            : '560px'
        }
        mx="auto"
        bg="bg.panel"
        borderWidth="1px"
        borderColor="border.subtle"
        borderRadius="lg"
        p={{ base: 5, md: 7 }}
      >
        <Flex justify="center" mb={{ base: 4, md: 6 }}>
          <Image
            src={mangoLogo}
            alt={t('secureFaxPage.logoAlt')}
            w="100%"
            maxW="220px"
            h="auto"
          />
        </Flex>

        {viewState === 'checking_status' ? (
          <Stack gap={2} alignItems="center" textAlign="center">
            <Heading size="lg">{t('secureFaxPage.loading.title')}</Heading>
            <Text color="fg.muted">
              {t('secureFaxPage.loading.description')}
            </Text>
          </Stack>
        ) : null}

        {viewState === 'status_invalid' ? renderStatusInvalidState() : null}

        {viewState === 'awaiting_password' ||
        viewState === 'submitting_password'
          ? renderPasswordForm()
          : null}

        {viewState === 'download_ready' ? renderDownloadReadyState() : null}

        {viewState === 'download_fetch_error'
          ? renderDownloadFetchErrorState()
          : null}
      </Box>
    </Flex>
  )
}
